/**
 * Iter52.78 · Rama 40 · Control Geotab.
 * Sube el CSV mensual de Geotab (MyAdmin → Monthly Bills), lo cruza con
 * lo que la app ya tiene y muestra qué cuadra y qué hay que revisar.
 * Solo lectura: desde aquí no se aplica ningún cambio.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Download, FileSpreadsheet, Search, UploadCloud } from 'lucide-react';
import { saasApi, errText } from './api';
import { useDescargas } from './useDescarga';
import FullReport from './FullReport';

const GRUPOS = [
  ['P', 'Permitidos', 'bg-sky-50 border-sky-200 text-sky-900'],
  ['1', 'Cuadran', 'bg-emerald-50 border-emerald-200 text-emerald-900'],
  ['2', 'Geotab cobra y la app no', 'bg-rose-50 border-rose-200 text-rose-900'],
  ['3', 'La app cobra y Geotab no factura', 'bg-amber-50 border-amber-200 text-amber-900'],
  ['4a', 'Plan distinto', 'bg-amber-50 border-amber-200 text-amber-900'],
  ['4b', 'Coste distinto (mismo plan)', 'bg-amber-50 border-amber-200 text-amber-900'],
  ['5i', 'IDs a un carácter', 'bg-slate-50 border-slate-200 text-slate-800'],
  ['5ii', 'La app cobra sin ID Geotab', 'bg-slate-50 border-slate-200 text-slate-800'],
  ['SIN_CLASIFICAR', 'Sin clasificar', 'bg-slate-50 border-slate-200 text-slate-600'],
];

const eur = (v) => `${Number(v || 0).toFixed(2)} €`;

// Iter52.97 · Texto corto de cada motivo permitido (desglose de P).
const MOTIVOS = {
  R82_ACTIVACION_DEL_MES: 'Activado en el mes',
  R83_COBRO_POR_FECHA_LIMITE: 'Cobro por fecha límite',
  R84_CORTESIA_O_PROMOCION: 'Cortesía o promoción',
  R85_USO_EN_SUSPEND: 'Uso en Suspend',
  R86_COBRO_MES_SIGUIENTE: 'Cobra el mes siguiente',
  R87_EQUIPO_PROPIO: 'Equipo propio',
};

export default function ControlGeotab(){
  const [file,setFile]=useState(null);
  const [busy,setBusy]=useState(false);
  const [analisis,setAnalisis]=useState(null);
  const [historico,setHistorico]=useState([]);
  const [grupo,setGrupo]=useState('2');
  const [detalle,setDetalle]=useState(null);
  const [q,setQ]=useState('');
  const [etiqueta,setEtiqueta]=useState('');
  // Iter52.98 · C · Filtro por motivo permitido (drill-down de Flota propia).
  const [motivo,setMotivo]=useState('');
  const [page,setPage]=useState(1);
  // Iter52.83 · Subpestaña Full Report con su insignia de pendientes.
  const [sub,setSub]=useState('factura');
  const [pendientesFR,setPendientesFR]=useState(0);

  useEffect(()=>{
    saasApi.get('/saas/admin/control-geotab/full-report/analisis')
      .then(r=>setPendientesFR(r.data?.items?.[0]?.pendientes_aprobar||0))
      .catch(()=>setPendientesFR(0));
  },[sub]);

  const cargarHistorico=async()=>{
    try{ const r=await saasApi.get('/saas/admin/control-geotab/analisis');
      setHistorico(r.data?.items||[]);
      if(!analisis && (r.data?.items||[]).length){
        const ultimo=r.data.items[0];
        const d=await saasApi.get(`/saas/admin/control-geotab/analisis/${ultimo.id}`);
        setAnalisis(d.data);
      }
    }catch(e){ toast.error(errText(e)); }
  };
  useEffect(()=>{cargarHistorico();/* eslint-disable-next-line */},[]);

  const analizar=async()=>{
    if(!file) return toast.error('Selecciona el CSV de Monthly Bills');
    setBusy(true); setDetalle(null);
    try{
      const fd=new FormData(); fd.append('csv_file', file);
      const r=await saasApi.post('/saas/admin/control-geotab/analizar', fd);
      setAnalisis(r.data); setPage(1);
      toast.success(`Analizado ${r.data.periodo} · ${r.data.filas} filas · ${r.data.ids_distintos} IDs`);
      cargarHistorico();
    }catch(e){ toast.error(errText(e), {duration: 8000}); }
    finally{ setBusy(false); }
  };

  const cargarGrupo=async(g, p=1, texto=q, etq=etiqueta, mot='')=>{
    if(!analisis) return;
    setGrupo(g); setPage(p); setEtiqueta(etq); setMotivo(mot);
    try{
      const r=await saasApi.get(`/saas/admin/control-geotab/analisis/${analisis.id}/grupo/${g}`,
        {params:{q: texto||undefined, etiqueta: etq||undefined, motivo: mot||undefined, page: p, page_size: 50}});
      setDetalle(r.data);
    }catch(e){ toast.error(errText(e)); }
  };
  useEffect(()=>{ if(analisis) cargarGrupo(grupo,1,'',''); /* eslint-disable-next-line */ },[analisis?.id]);

  // Iter52.91 · B2 · Las descargas avisan de que están trabajando.
  const {descargando,conEstadoDescarga}=useDescargas();
  const descargar=()=>conEstadoDescarga('excel-grupos',async()=>{
    try{
      const r=await saasApi.get(`/saas/admin/control-geotab/analisis/${analisis.id}/excel`,
        {responseType:'blob'});
      const url=URL.createObjectURL(new Blob([r.data]));
      const a=document.createElement('a'); a.href=url;
      a.download=`Control_Geotab_${analisis.periodo}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    }catch(e){ toast.error(errText(e)); }
  });

  const descargarAbono=(clienteId)=>conEstadoDescarga(`abono-${clienteId||'todos'}`,async()=>{
    try{
      const r=await saasApi.get(`/saas/admin/control-geotab/analisis/${analisis.id}/abono`,
        {params: clienteId?{clientes: clienteId}:{}, responseType:'blob'});
      const url=URL.createObjectURL(new Blob([r.data]));
      const a=document.createElement('a'); a.href=url;
      a.download=`Abonos_Suspensiones_${analisis.periodo}.zip`; a.click();
      URL.revokeObjectURL(url);
    }catch(e){ toast.error(errText(e)); }
  });

  const columnas=useMemo(()=>{
    const filas=detalle?.items||[];
    const out=[]; filas.forEach(f=>Object.keys(f).forEach(k=>{ if(!out.includes(k)) out.push(k); }));
    return out;
  },[detalle]);
  const totalPags=detalle? Math.max(1, Math.ceil(detalle.total/detalle.page_size)) : 1;

  return <div className="space-y-4" data-testid="tab-control-geotab">
    {/* Iter52.83 · Subpestañas: factura mensual y Full Report */}
    <div className="flex gap-2">
      {[['factura','Factura mensual'],['full-report','Full Report']].map(([k,l])=>
        <button key={k} onClick={()=>setSub(k)} data-testid={`control-geotab-sub-${k}`}
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${sub===k?'bg-emerald-600 text-white border-emerald-600':'bg-white text-slate-600'}`}>
          {l}{k==='full-report'&&pendientesFR>0?` · ${pendientesFR}`:''}
        </button>)}
    </div>
    {sub==='full-report' && <FullReport/>}
    {sub==='factura' && <>
    <div className="bg-white border rounded-2xl p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-black flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-emerald-600"/>Control Geotab</h2>
          <p className="text-sm text-slate-500 mt-1">Sube el <b>CSV original</b> de MyAdmin → Monthly Bills. Se cruza por ID Geotab con lo que la app cobra este mes. <b>No cambia nada</b>: las correcciones siguen por el Excel de actualización, Dispositivos → Editar y Catálogo Geotab.</p>
        </div>
      </div>
      <div className="flex gap-2 mt-4 flex-wrap items-center">
        <input data-testid="control-geotab-file" type="file" accept=".csv" className="input max-w-sm"
          onChange={e=>setFile(e.target.files?.[0]||null)}/>
        <button data-testid="btn-control-geotab-analizar" className="btn-primary" disabled={!file||busy} onClick={analizar}>
          <UploadCloud className="w-4 h-4"/>{busy?'Analizando…':'Analizar'}
        </button>
        {analisis && <button data-testid="btn-control-geotab-excel" className="btn-secondary ml-auto" onClick={descargar}>
          <Download className="w-4 h-4"/>Descargar Excel
        </button>}
      </div>
    </div>

    {analisis && <>
      <div className="bg-white border rounded-2xl p-5" data-testid="control-geotab-resumen">
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[['Periodo',analisis.periodo],['Cuenta',analisis.cuenta||'—'],
            ['Filas',analisis.filas],['IDs distintos',analisis.ids_distintos],
            ['Coste total',eur(analisis.coste_total)],
            ['Costes sin dispositivo',eur(analisis.coste_sin_dispositivo)]].map(([l,v])=>
            <div key={l} className="border rounded-xl p-3"><p className="text-xs uppercase text-slate-500">{l}</p><p className="text-xl font-black" data-testid={`cg-kpi-${l}`}>{v}</p></div>)}
        </div>
        <p className="text-xs text-slate-400 mt-2">{analisis.filename} · {String(analisis.created_at||'').slice(0,19).replace('T',' ')} · {analisis.suscripciones_evaluadas} suscripciones evaluadas</p>
        {(analisis.avisos||[]).map((a,i)=><p key={i} className="text-xs text-amber-700 mt-1 flex gap-1 items-center"><AlertTriangle className="w-3 h-3"/>{a}</p>)}
      </div>

      {/* Iter52.98 · C · KPI «Flota propia» · equipos de clientes internos
          que Geotab factura (motivo permitido R87). Solo lectura. */}
      {!!analisis.flota_propia && <button
        data-testid="cg-kpi-flota-propia"
        onClick={()=>cargarGrupo('P',1,'','','R87_EQUIPO_PROPIO')}
        className="w-full text-left bg-white border-2 border-sky-300 rounded-2xl p-5 hover:bg-sky-50 transition-colors">
        <p className="text-xs uppercase tracking-wide text-sky-700 font-bold">Flota propia</p>
        <p className="text-2xl font-black text-sky-900 mt-1">
          {analisis.flota_propia.n_equipos||0} equipos · {eur(analisis.flota_propia.coste_geotab_mes)} Geotab/mes
        </p>
        <p className="text-xs text-slate-500 mt-1">Equipos propios (cliente interno) que Geotab factura · coste asumido. Pulsa para ver el listado.</p>
      </button>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="control-geotab-grupos">        {GRUPOS.map(([g,label,cls])=>{
          const r=analisis.resumen?.[g]||{};
          const ingreso=Number(r.ingreso_app_mes||0), coste=Number(r.coste_geotab_mes||0);
          return <button key={g} data-testid={`cg-grupo-${g}`} onClick={()=>cargarGrupo(g,1,'')}
            className={`text-left border rounded-xl p-3 transition-colors hover:brightness-95 ${cls} ${grupo===g?'ring-2 ring-offset-1 ring-slate-400':''}`}>
            <p className="text-xs font-bold uppercase">{g} · {label}</p>
            <p className="text-2xl font-black">{r.n_ids||0}</p>
            <p className="text-xs">{coste>0?`${eur(coste)} de Geotab`:(ingreso>0?`${eur(ingreso)} de ingreso app`:'—')}</p>
            <p className="text-[11px] mt-1 opacity-80" data-testid={`cg-grupo-${g}-parciales`}>
              Mes completo {r.completo?.n_ids||0} · Parcial {r.parcial?.n_ids||0}
              {r.activados_en_el_mes>0?` · activados en el mes ${r.activados_en_el_mes}`:''}
            </p>
          </button>;
        })}
      </div>

      {/* Iter52.97 · Desglose de «Permitidos» por motivo · cada chip filtra. */}
      {!!Object.keys(analisis.resumen?.P?.motivos||{}).length &&
        <div className="bg-white border rounded-2xl p-4" data-testid="cg-permitidos-motivos">
          <p className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-2">Permitidos por motivo</p>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(analisis.resumen.P.motivos).map(([m,v])=>
              <button key={m} data-testid={`cg-motivo-${m}`}
                onClick={()=>cargarGrupo('P',1,'','',m)}
                className={`text-left border rounded-xl px-3 py-2 text-xs transition-colors hover:brightness-95 ${motivo===m?'bg-sky-100 border-sky-300':'bg-sky-50 border-sky-200'}`}>
                <span className="font-bold text-sky-900">{MOTIVOS[m]||m}</span>
                <span className="block text-sky-800">{v.n_ids} IDs · {eur(v.coste_geotab_mes)}</span>
              </button>)}
          </div>
        </div>}

      <div className="bg-white border rounded-2xl overflow-hidden" data-testid="control-geotab-detalle">
        <div className="px-5 py-3 border-b flex items-center justify-between gap-3 flex-wrap">
          <span className="font-black">{detalle?.etiqueta||'Detalle'} · {detalle?.total||0} IDs</span>
          {!!motivo && <span data-testid="cg-chip-flota-propia"
            className="text-xs font-bold px-2 py-1 rounded-full bg-sky-100 text-sky-800 flex items-center gap-2">
            {MOTIVOS[motivo]||motivo}
            <button className="underline" data-testid="cg-quitar-filtro-flota-propia"
              onClick={()=>cargarGrupo('P',1,q,etiqueta,'')}>quitar filtro</button>
          </span>}
          <div className="flex gap-2 items-center">
            <div className="relative"><Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400"/>
              <input data-testid="cg-buscar" className="input pl-9 max-w-[260px]" placeholder="ID o cliente…" value={q}
                onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==='Enter'&&cargarGrupo(grupo,1,q,etiqueta,motivo)}/></div>
            <select data-testid="cg-filtro-parcial" className="input max-w-[200px]" value={etiqueta}
              onChange={e=>cargarGrupo(grupo,1,q,e.target.value,motivo)}>
              <option value="">Completo y parcial</option>
              <option value="COMPLETO">Solo mes completo</option>
              <option value="PARCIAL">Solo parciales</option>
              <option value="PARCIAL_ENTRA">Parcial · entra</option>
              <option value="PARCIAL_SALE">Parcial · sale</option>
              <option value="PARCIAL_ENTRA_SALE">Parcial · entra y sale</option>
            </select>
            <button className="btn-secondary" data-testid="cg-buscar-btn" onClick={()=>cargarGrupo(grupo,1,q,etiqueta,motivo)}>Buscar</button>
          </div>
        </div>
        <div className="overflow-auto max-h-[520px]">
          <table className="table text-xs">
            <thead className="bg-slate-50 sticky top-0"><tr>{columnas.map(c=><th key={c}>{c.replace(/_/g,' ')}</th>)}</tr></thead>
            <tbody>
              {(detalle?.items||[]).map((f,i)=><tr key={i} data-testid={`cg-fila-${f.id_geotab||f.suscripcion_id||i}`}>
                {columnas.map(c=><td key={c} className={c.includes('coste')||c.includes('ingreso')||c.includes('diferencia')?'text-right tabular-nums':''}>
                  {typeof f[c]==='boolean'?(f[c]?'Sí':'No'):(f[c]??'—')}
                </td>)}
              </tr>)}
              {!(detalle?.items||[]).length && <tr><td colSpan={Math.max(columnas.length,1)} className="text-center py-8 text-slate-400">Sin filas en este grupo</td></tr>}
            </tbody>
          </table>
        </div>
        {totalPags>1 && <div className="px-5 py-3 border-t flex gap-2 items-center justify-end text-xs">
          <button className="btn-secondary" disabled={page<=1} onClick={()=>cargarGrupo(grupo,page-1,q,etiqueta,motivo)}>Anterior</button>
          <span>Página {page} de {totalPags}</span>
          <button className="btn-secondary" data-testid="cg-pag-siguiente" disabled={page>=totalPags} onClick={()=>cargarGrupo(grupo,page+1,q,etiqueta,motivo)}>Siguiente</button>
        </div>}
      </div>

      {/* Iter52.78b · Suspensiones · abono a clientes de uso (regla 85). */}
      {!!analisis.suspensiones && <div className="bg-white border rounded-2xl p-5" data-testid="control-geotab-suspensiones">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-black">Suspensiones (clientes con abono por suspensión)</h3>
            <p className="text-xs text-slate-500 mt-1">
              Abono por la diferencia entre tarifa activa y tarifa de suspensión del día 1.
              {' '}{analisis.suspensiones.totales?.lineas_suspend||0} líneas Suspend ·
              {' '}{analisis.suspensiones.totales?.equipos_con_abono||0} equipos con abono ·
              {' '}<b>{eur(analisis.suspensiones.totales?.abono_total)}</b>. Solo cálculo: no crea ningún abono en la app.
            </p>
          </div>
          {!!(analisis.suspensiones.clientes||[]).length &&
            <button className="btn-secondary disabled:opacity-60 disabled:cursor-not-allowed" data-testid="btn-abono-todos" disabled={descargando('abono-todos')} onClick={()=>descargarAbono()}>
              <Download className="w-4 h-4"/>{descargando('abono-todos')?'Generando albaranes…':'Descargar albaranes de abono'}
            </button>}
        </div>
        {(analisis.suspensiones.clientes||[]).map(cli=>
          <div key={cli.cliente_id} className="mt-4 border rounded-xl overflow-hidden" data-testid={`cg-abono-cliente-${cli.cliente_id}`}>
            <div className="px-4 py-2 bg-slate-50 flex items-center justify-between gap-3 flex-wrap">
              <span className="font-bold text-sm">{cli.razon_social} · {cli.equipos} equipos · {Number(cli.unidades||0).toFixed(4)} unidades · <span className="text-emerald-700">{eur(cli.abono_total)}</span></span>
              <button className="link text-xs disabled:opacity-60 disabled:cursor-not-allowed" data-testid={`btn-abono-${cli.cliente_id}`} disabled={descargando(`abono-${cli.cliente_id}`)} onClick={()=>descargarAbono(cli.cliente_id)}>{descargando(`abono-${cli.cliente_id}`)?'Generando albaranes…':'Descargar albarán de abono'}</button>
            </div>
            <div className="overflow-auto max-h-72">
              <table className="table text-xs">
                <thead className="bg-white sticky top-0"><tr><th>ID Geotab</th><th>Matrícula</th><th>Plan Suspend</th><th className="text-right">Unidades</th><th className="text-right">Activa</th><th className="text-right">Suspendida</th><th className="text-right">Diferencia</th><th className="text-right">Abono</th></tr></thead>
                <tbody>{cli.detalle.map(f=><tr key={f.id_geotab}>
                  <td>{f.id_geotab}</td><td>{f.matricula||'—'}</td><td>{f.plan_suspend}</td>
                  <td className="text-right tabular-nums">{Number(f.unidades||0).toFixed(4)}</td>
                  <td className="text-right tabular-nums">{eur(f.tarifa_activa)}</td>
                  <td className="text-right tabular-nums">{eur(f.tarifa_suspendida)}</td>
                  <td className="text-right tabular-nums">{eur(f.diferencia)}</td>
                  <td className="text-right tabular-nums font-bold">{eur(f.abono)}</td>
                </tr>)}</tbody>
              </table>
            </div>
          </div>)}
        {!!(analisis.suspensiones.sin_abono||[]).length && <div className="mt-4" data-testid="cg-suspend-sin-abono">
          <h4 className="font-bold text-sm mb-1">Suspend sin abono · {analisis.suspensiones.sin_abono.length}</h4>
          <div className="overflow-auto max-h-72">
            <table className="table text-xs"><thead className="bg-slate-50 sticky top-0"><tr><th>ID Geotab</th><th>Cliente</th><th>Plan Suspend</th><th className="text-right">Unidades</th><th>Motivo</th></tr></thead>
              <tbody>{analisis.suspensiones.sin_abono.map((f,i)=><tr key={i}>
                <td>{f.id_geotab}</td><td>{f.cliente_app||f.customer||'—'}</td><td>{f.plan_suspend}</td>
                <td className="text-right tabular-nums">{Number(f.unidades||0).toFixed(4)}</td><td>{f.motivo}</td>
              </tr>)}</tbody></table>
          </div>
        </div>}
      </div>}

      {!!(analisis.sin_dispositivo||[]).length && <div className="bg-white border rounded-2xl p-5" data-testid="control-geotab-sin-dispositivo">
        <h3 className="font-black mb-2">Costes sin dispositivo · {eur(analisis.coste_sin_dispositivo)}</h3>
        <p className="text-xs text-slate-500 mb-3">Coste real de la factura que no pertenece a ningún equipo (add-ins y similares). Informativo.</p>
        <table className="table text-xs"><thead><tr><th>Línea</th><th>Concepto</th><th>Base de datos</th><th className="text-right">Cantidad</th><th className="text-right">Coste</th></tr></thead>
          <tbody>{analisis.sin_dispositivo.map((s,i)=><tr key={i}><td>{s.linea}</td><td>{s.concepto}</td><td>{s.base_datos||'—'}</td><td className="text-right">{s.cantidad}</td><td className="text-right tabular-nums">{eur(s.coste)}</td></tr>)}</tbody></table>
      </div>}
    </>}

    {!!historico.length && <div className="bg-white border rounded-2xl p-5" data-testid="control-geotab-historico">
      <h3 className="font-black mb-3">Análisis anteriores</h3>
      <table className="table text-xs"><thead><tr><th>Fecha</th><th>Periodo</th><th>Fichero</th><th className="text-right">Filas</th><th className="text-right">IDs</th><th className="text-right">Coste</th><th></th></tr></thead>
        <tbody>{historico.map(h=><tr key={h.id}>
          <td>{String(h.created_at||'').slice(0,19).replace('T',' ')}</td>
          <td>{h.periodo}</td><td>{h.filename}</td>
          <td className="text-right">{h.filas}</td><td className="text-right">{h.ids_distintos}</td>
          <td className="text-right tabular-nums">{eur(h.coste_total)}</td>
          <td><button className="link" data-testid={`cg-abrir-${h.id}`} onClick={async()=>{
            try{ const d=await saasApi.get(`/saas/admin/control-geotab/analisis/${h.id}`); setAnalisis(d.data); }
            catch(e){ toast.error(errText(e)); }
          }}>Abrir</button></td>
        </tr>)}</tbody></table>
    </div>}
    </>}
  </div>;
}
